//@ts-check
/**
 * Photos of things: shrink on the phone, upload straight to S3, show through a signed URL.
 *
 * Every photo is re-encoded here before it leaves the device, for two reasons that both matter in
 * a shop. A 12-megapixel camera frame is several megabytes, and one bar of signal will not send
 * that before the impulse passes; the long edge is capped so an upload is a few hundred kilobytes.
 * And re-encoding through a canvas drops the EXIF block — GPS coordinates included. Photos are
 * private (guides/PHOTOS.md), but one that is ever shared should not carry where it was taken.
 * The upload URL is signed for JPEG only, so a photo that skipped this step cannot be uploaded.
 *
 * Rotation is left to the browser: iOS Safari and every current engine apply EXIF orientation when
 * decoding into an <img>, so what is drawn onto the canvas is already upright and the re-encoded
 * file needs no orientation tag. Spike 1.2 checks that on the device rather than trusting it.
 */
import ky from "ky";
import { putToPresignedUrl } from "$lib/api.js";
import { getfetchopts } from "$lib/utils/utils.js";
import { apiBase } from "$lib/env.js";

// Relative on the web; the app host inside the iOS app ($lib/env.js).
const basePath = apiBase();

/** Longest edge of an uploaded photo, in pixels. Plenty for a phone screen at 3x. */
const MAX_EDGE = 1600;
const JPEG_QUALITY = 0.82;

/**
 * @typedef {object} PreparedPhoto
 * @property {Blob} blob            the JPEG to upload
 * @property {string} previewUrl    an object URL for showing it before and while it uploads
 * @property {PhotoFacts} facts     what arrived from the camera, for the capture spike
 */

/**
 * @typedef {object} PhotoFacts
 * @property {string} type
 * @property {number} bytes
 * @property {number} width          as decoded — i.e. after the browser applied any rotation
 * @property {number} height
 * @property {number | null} exifOrientation  1 = upright; null when there is no EXIF block
 * @property {boolean} exifGps       whether the original carried GPS coordinates
 * @property {number} ageMs          how old the file claims to be: near zero for a fresh shot
 * @property {number} outWidth
 * @property {number} outHeight
 * @property {number} outBytes
 * @property {number} prepareMs      time spent decoding and re-encoding on the device
 */

/**
 * Turn whatever the camera or library handed over into an upload-ready JPEG.
 *
 * @param {File} file
 * @returns {Promise<PreparedPhoto>}
 */
export async function preparePhoto(file) {
  const started = performance.now();
  const exif = readExif(await file.slice(0, 256 * 1024).arrayBuffer());

  const sourceUrl = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = sourceUrl;
    await img.decode();

    const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
    const outWidth = Math.round(img.naturalWidth * scale);
    const outHeight = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement("canvas");
    canvas.width = outWidth;
    canvas.height = outHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("No canvas context");
    // JPEG has no transparency, and a canvas starts transparent-black: a product shot on a
    // transparent PNG — the usual kind on a shop's page — would come out on black. White is what
    // the shop meant.
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, outWidth, outHeight);
    ctx.drawImage(img, 0, 0, outWidth, outHeight);

    const blob = await new Promise((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Could not encode photo"))), "image/jpeg", JPEG_QUALITY)
    );

    return {
      blob,
      previewUrl: URL.createObjectURL(blob),
      facts: {
        type: file.type || "unknown",
        bytes: file.size,
        width: img.naturalWidth,
        height: img.naturalHeight,
        exifOrientation: exif.orientation,
        exifGps: exif.gps,
        ageMs: Date.now() - file.lastModified,
        outWidth,
        outHeight,
        outBytes: blob.size,
        prepareMs: Math.round(performance.now() - started),
      },
    };
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
}

/**
 * The first image in a paste, if the paste carries one.
 *
 * A scripted clipboard read is dead on iOS (spike 1.6), but a paste the user starts from a text
 * field's own menu is a different path: the browser hands the page whatever the clipboard holds.
 * A text field cannot show an image, so without this the image is silently dropped.
 *
 * @param {ClipboardEvent} event
 * @returns {File | null}
 */
export function imageFromPaste(event) {
  const data = event.clipboardData;
  if (!data) return null;
  for (const item of data.items || []) {
    if (item.kind === "file" && item.type.startsWith("image/")) return item.getAsFile();
  }
  for (const file of data.files || []) {
    if (file.type.startsWith("image/")) return file;
  }
  return null;
}

/**
 * Upload a prepared photo and return the key to store on the thing. The things API decides where
 * it goes — always inside the caller's own folder.
 *
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export async function uploadPhoto(blob) {
  const fetchopts = await getfetchopts();
  const { uploadUrl, imageKey } = await ky.post(`${basePath}/api/things/photos/uploadurl`, fetchopts).json();
  await putToPresignedUrl({ uploadUrl, blob, contentType: "image/jpeg" });
  return imageKey;
}

/**
 * The signing material for viewing this user's photos: one CloudFront signature, appended to any
 * photo URL in their folder. Held in memory only — it is a week's read access to every photo the
 * user has, so it is not left in storage for anything else on the page to find.
 *
 * `photoUrl` reads it, so every photo on screen re-renders the moment it arrives or is replaced.
 */
const access = $state({ query: "", expires: 0 });

/** Ask again once less than this is left, so a signature never runs out while the app is open. */
const REFRESH_BEFORE_S = 24 * 60 * 60;
/** Floor between forced refreshes, so a photo that is genuinely missing cannot loop. */
const RETRY_AFTER_MS = 60_000;

/** @type {Promise<void> | null} */
let inflight = null;
let lastForced = 0;

/**
 * Make sure there is a signature with time left on it. Cheap to call often — on start, on
 * returning to the app — because it does nothing when the current one is fresh.
 *
 * @param {{ force?: boolean }} [options] force: fetch a new one regardless, e.g. after a photo
 *   failed to load because the key was rotated
 */
export function ensurePhotoAccess({ force = false } = {}) {
  const now = Date.now();
  if (force) {
    if (now - lastForced < RETRY_AFTER_MS) return inflight ?? Promise.resolve();
    lastForced = now;
  } else if (access.query && access.expires - now / 1000 > REFRESH_BEFORE_S) {
    return Promise.resolve();
  }
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const fetchopts = await getfetchopts();
      const res = await ky.get(`${basePath}/api/things/photos/access`, fetchopts).json();
      access.query = res.query;
      access.expires = res.expires;
    } catch (err) {
      console.error("Could not get photo access", err);
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

/**
 * The URL a stored photo can be shown from: same-origin (`/photos/...` on the app's own host),
 * carrying the signature. Inside the iOS app, the same path on the app host — an `<img>` needs no
 * CORS and the signature is in the query, so no cookie is involved either way. Empty until a
 * signature has arrived — an unsigned request would only come back 403.
 *
 * @param {string} key
 */
export function photoUrl(key) {
  if (!key || !access.query) return "";
  return `${basePath}/${key}?${access.query}`;
}

/**
 * Read the two EXIF facts the capture spike asks about — orientation and whether GPS is present —
 * from the start of a JPEG. Not a general EXIF reader: anything it does not recognise, including a
 * HEIC file, comes back as "no EXIF" rather than an error.
 *
 * @param {ArrayBuffer} buffer
 * @returns {{ orientation: number | null, gps: boolean }}
 */
function readExif(buffer) {
  const none = { orientation: null, gps: false };
  const view = new DataView(buffer);
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return none;

  let offset = 2;
  while (offset + 4 <= view.byteLength) {
    const marker = view.getUint16(offset);
    const size = view.getUint16(offset + 2);
    // APP1 carrying "Exif\0\0"
    if (marker === 0xffe1 && offset + 10 <= view.byteLength && view.getUint32(offset + 4) === 0x45786966) {
      return readTiff(view, offset + 10);
    }
    if ((marker & 0xff00) !== 0xff00 || marker === 0xffda) return none;
    offset += 2 + size;
  }
  return none;
}

/**
 * @param {DataView} view
 * @param {number} tiff offset of the TIFF header inside the buffer
 */
function readTiff(view, tiff) {
  const result = { orientation: /** @type {number | null} */ (null), gps: false };
  try {
    const little = view.getUint16(tiff) === 0x4949;
    const u16 = (/** @type {number} */ o) => view.getUint16(o, little);
    const u32 = (/** @type {number} */ o) => view.getUint32(o, little);

    const ifd0 = tiff + u32(tiff + 4);
    const count = u16(ifd0);
    for (let i = 0; i < count; i++) {
      const entry = ifd0 + 2 + i * 12;
      const tag = u16(entry);
      if (tag === 0x0112) result.orientation = u16(entry + 8);
      // A GPS IFD pointer alone is not proof of coordinates; iOS writes one with only a timestamp
      // when location is off. Count it as GPS only if it holds a latitude.
      if (tag === 0x8825) {
        const gpsIfd = tiff + u32(entry + 8);
        const gpsCount = u16(gpsIfd);
        for (let j = 0; j < gpsCount; j++) {
          if (u16(gpsIfd + 2 + j * 12) === 0x0002) result.gps = true;
        }
      }
    }
  } catch {
    // A truncated or odd EXIF block is not worth failing a capture over.
  }
  return result;
}
